import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Paper,
  CircularProgress
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getEquipmentSpecParsedBySpecId, getLastEquipmentSpecParsed } from "components/axios/equipmentSpecAxios";
import { useTranslation } from "react-i18next";

// ----------------------
// spec 안에 실제 표시할 데이터가 있는지 검사
// ----------------------
const hasRenderableSpec = (spec) => {
  if (!spec) return false;

  const hasValue =
    spec.value !== undefined &&
    spec.value !== null &&
    spec.value !== "" &&
    spec.value !== "-";

  const hasChildren =
    spec.subSpecs && spec.subSpecs.some(hasRenderableSpec);

  return hasValue || hasChildren;
};

// ----------------------
// Spec 재귀
// ----------------------
const SpecView = ({ spec, depth = 0 }) => {
  if (!hasRenderableSpec(spec)) return null;

  const hasChildren =
    spec.subSpecs && spec.subSpecs.some(hasRenderableSpec);
  const hasValue =
    spec.value !== undefined &&
    spec.value !== null &&
    spec.value !== "" &&
    spec.value !== "-";

  return (
    <Box sx={{ ml: depth * 2, mt: 1 }}>
      {(hasChildren || hasValue) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            py: 0.5
          }}
        >
          <Typography
            variant={depth === 0 ? "subtitle2" : "body2"}
            sx={{ fontWeight: hasChildren ? 600 : 400 }}
          >
            {spec.name} ({spec.key})
          </Typography>

          {hasValue && (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {spec.value} {spec.unit}
            </Typography>
          )}
        </Box>
      )}

      {hasChildren &&
        spec.subSpecs
          .filter(hasRenderableSpec)
          .map((child, idx) => (
            <SpecView key={idx} spec={child} depth={depth + 1} />
          ))}
    </Box>
  );
};

// const sampleData = [
//   {
//     "id": 5,
//     "code": "trolleyUnit",
//     "name": "트롤리부",
//     "locations": [
//       {
//         "id": 12,
//         "code": "T1",
//         "name": "트롤리",
//         "parts": [
//           {
//             "id": null,
//             "code": "WIRE1",
//             "name": "와이어",
//             "sensorPositions": [
//               {
//                 "sensorPosition": "N_1",
//                 "specs": [
//                   {
//                     "key": "trolleyUnit",
//                     "name": "트롤리부",
//                     "subSpecs": [
//                       {
//                         "key": "trolley",
//                         "name": "트롤리",
//                         "value": "AC Type",
//                         "unit": "-",
//                         "subSpecs": [
//                           {
//                             "key": "hoistWeight",
//                             "name": "권상하중",
//                             "value": "20.0",
//                             "unit": "ton"
//                           },
//                           {
//                             "key": "trolleyNum",
//                             "name": "트롤리 개수",
//                             "value": "1",
//                             "unit": "ea"
//                           },
//                           {
//                             "key": "hookNum",
//                             "name": "후크 개수",
//                             "value": "1",
//                             "unit": "ea"
//                           }
//                         ]
//                       },
//                       {
//                         "key": "wire",
//                         "name": "와이어",
//                         "subSpecs": [
//                           {
//                             "key": "wireLength",
//                             "name": "와이어 길이",
//                             "value": "35",
//                             "unit": "m"
//                           }
//                         ]
//                       }
//                     ]
//                   }
//                 ]
//               },
//               {
//                 "sensorPosition": "N_2",
//                 "specs": [
//                   {
//                     "key": "trolleyUnit",
//                     "name": "트롤리부",
//                     "subSpecs": [
//                       {
//                         "key": "trolley",
//                         "name": "트롤리",
//                         "subSpecs": [
//                           {
//                             "key": "hoistWeight",
//                             "name": "권상하중",
//                             "value": "20.0",
//                             "unit": "ton"
//                           },
//                           {
//                             "key": "trolleyNum",
//                             "name": "트롤리 개수",
//                             "value": "1",
//                             "unit": "ea"
//                           },
//                           {
//                             "key": "hookNum",
//                             "name": "후크 개수",
//                             "value": "1",
//                             "unit": "ea"
//                           }
//                         ]
//                       },
//                       {
//                         "key": "wire",
//                         "name": "와이어",
//                         "subSpecs": [
//                           {
//                             "key": "wireLength",
//                             "name": "와이어 길이",
//                             "value": "35",
//                             "unit": "m"
//                           }
//                         ]
//                       }
//                     ]
//                   }
//                 ]
//               }
//             ]
//           },
//           {
//             "id": null,
//             "code": "TMOTOR1",
//             "name": "트롤리 모터 1",
//             "sensorPositions": []
//           },
//           {
//             "id": null,
//             "code": "CMOTOR1",
//             "name": "크레인 모터 1",
//             "sensorPositions": [
//               {
//                 "sensorPosition": "V_1",
//                 "specs": [
//                   {
//                     "key": "drivingSection",
//                     "name": "구동부",
//                     "subSpecs": [
//                       {
//                         "key": "motor",
//                         "name": "전동기",
//                         "subSpecs": [
//                           {
//                             "key": "modelName",
//                             "name": "모델명",
//                             "value": "HST_MOTOR",
//                             "unit": ""
//                           },
//                           {
//                             "key": "power",
//                             "name": "동력",
//                             "value": "5.5",
//                             "unit": "kW"
//                           },
//                           {
//                             "key": "f_L",
//                             "name": "주파수",
//                             "value": "60.0",
//                             "unit": "Hz"
//                           },
//                           {
//                             "key": "efficiency",
//                             "name": "효율",
//                             "value": "84.7",
//                             "unit": "%"
//                           },
//                           {
//                             "key": "mVolt",
//                             "name": "전압",
//                             "value": "380.0",
//                             "unit": "V"
//                           },
//                           {
//                             "key": "electricCurrent",
//                             "name": "전류",
//                             "value": "16.2",
//                             "unit": "A"
//                           },
//                           {
//                             "key": "rpm",
//                             "name": "회전속도",
//                             "value": "1120.0",
//                             "unit": "rpm"
//                           },
//                           {
//                             "key": "poleNum",
//                             "name": "극수",
//                             "value": "6",
//                             "unit": "P"
//                           }
//                         ]
//                       },
//                       {
//                         "key": "motorBearingLoad",
//                         "name": "전동기 부하측 베어링",
//                         "subSpecs": [
//                           {
//                             "key": "manufacturer",
//                             "name": "제조사",
//                             "value": "KHRD",
//                             "unit": ""
//                           },
//                           {
//                             "key": "modelName",
//                             "name": "모델명",
//                             "value": "6307ZZ",
//                             "unit": ""
//                           },
//                           {
//                             "key": "ballsNum",
//                             "name": "볼 개수",
//                             "value": "8",
//                             "unit": "ea"
//                           },
//                           {
//                             "key": "ballsPcd",
//                             "name": "볼 PCD",
//                             "value": "13.494",
//                             "unit": "mm"
//                           },
//                           {
//                             "key": "bearingPcd",
//                             "name": "베어링 PCD",
//                             "value": "57.5",
//                             "unit": "mm"
//                           },
//                           {
//                             "key": "contactAngle",
//                             "name": "접촉각",
//                             "value": "0.0",
//                             "unit": "deg"
//                           }
//                         ]
//                       }
//                     ]
//                   }
//                 ]
//               }
//             ]
//           },
//           {
//             "id": null,
//             "code": "CMOTOR2",
//             "name": "크레인 모터 2",
//             "sensorPositions": [
//               {
//                 "sensorPosition": "V_1",
//                 "specs": [
//                   {
//                     "key": "drivingSection",
//                     "name": "구동부",
//                     "subSpecs": [
//                       {
//                         "key": "motor",
//                         "name": "전동기",
//                         "subSpecs": [
//                           {
//                             "key": "power",
//                             "name": "동력",
//                             "value": "17",
//                             "unit": "kW"
//                           },
//                           {
//                             "key": "rpm",
//                             "name": "회전속도",
//                             "value": "855.0",
//                             "unit": "rpm"
//                           }
//                         ]
//                       }
//                     ]
//                   }
//                 ]
//               }
//             ]
//           },
//           {
//             "id": null,
//             "code": "CREDUCER1",
//             "name": "크레인 감속기 1",
//             "sensorPositions": [
//               {
//                 "sensorPosition": "V_1",
//                 "specs": [
//                   {
//                     "key": "drivingSection",
//                     "name": "구동부",
//                     "subSpecs": [
//                       {
//                         "key": "reducer",
//                         "name": "감속기",
//                         "subSpecs": [
//                           {
//                             "key": "reducerStageNum",
//                             "name": "감속기 단수",
//                             "value": "-",
//                             "unit": ""
//                           }
//                         ]
//                       }
//                     ]
//                   }
//                 ]
//               }
//             ]
//           },
//           {
//             "id": null,
//             "code": "CREDUCER2",
//             "name": "크레인 감속기 2",
//             "sensorPositions": [
//               {
//                 "sensorPosition": "V_1",
//                 "specs": [
//                   {
//                     "key": "drivingSection",
//                     "name": "구동부",
//                     "subSpecs": [
//                       {
//                         "key": "reducer",
//                         "name": "감속기",
//                         "subSpecs": [
//                           {
//                             "key": "reducerStageNum",
//                             "name": "감속기 단수",
//                             "value": "-",
//                             "unit": ""
//                           }
//                         ]
//                       }
//                     ]
//                   }
//                 ]
//               }
//             ]
//           }
//         ]
//       }
//     ]
//   },
//   {
//     "id": null,
//     "code": "drivingSection",
//     "name": "구동부",
//     "locations": [
//       {
//         "id": null,
//         "code": "NONE",
//         "name": "기본",
//         "parts": [
//           {
//             "id": null,
//             "code": "DMOTOR1",
//             "name": "대형전동기 ",
//             "sensorPositions": []
//           }
//         ]
//       }
//     ]
//   }
// ];

export default function Specs ({equipmentId, specId, componentId, componentLocation}) {
    const { t } = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    useEffect(() => {
      const fetchData = async () => {
        // console.log(specId)
          try {
              setLoading(true);
              const res = await getEquipmentSpecParsedBySpecId(equipmentId, specId);
              setData(res.components);
              
          } catch (err) {
              console.error(err);
              setError(t('message.common.error.text.failed.load.data'));
          } finally {
              setLoading(false);
          }
      };

      if (equipmentId) {
          fetchData();
      }
    }, [equipmentId, specId]);

    // console.log(componentId, componentLocation)
    /* ---------- 대상 location 찾기 ---------- */
    const targetLocation = useMemo(() => {
        const group = data.find(g => g.id === componentId);
        if (!group) return null;
        return group.locations.find(l => l.id === componentLocation);
    }, [data, componentId, componentLocation]);

    /* ---------- sensor 없는 part 제거 ---------- */
    const visibleParts = useMemo(() => {
      if (!targetLocation) return [];

      return targetLocation.parts
        .map(part => {
          const filteredSensors = part.sensorPositions
            ?.map(sensor => ({
              ...sensor,
              specs: sensor.specs?.filter(hasRenderableSpec) || []
            }))
            .filter(sensor => sensor.specs.length > 0);

          return {
            ...part,
            sensorPositions: filteredSensors
          };
        })
        .filter(part => part.sensorPositions.length > 0);
    }, [targetLocation]);

    /* ---------- 펼침 상태 관리 ---------- */
    const [expandedMap, setExpandedMap] = useState({});

    /* 초기 전체 펼침 */
    useEffect(() => {
        const initialState = {};

        visibleParts.forEach(part => {
        initialState[`part-${part.code}`] = true;

        part.sensorPositions.forEach(sensor => {
            initialState[
            `part-${part.code}-sensor-${sensor.sensorPosition}`
            ] = true;
        });
        });

        setExpandedMap(initialState);
    }, [visibleParts]);

    /* 전체 펼침/닫기 */
    const handleToggleAll = expand => {
        const newState = {};

        visibleParts.forEach(part => {
        newState[`part-${part.code}`] = expand;

        part.sensorPositions.forEach(sensor => {
            newState[
            `part-${part.code}-sensor-${sensor.sensorPosition}`
            ] = expand;
        });
        });

        setExpandedMap(newState);
    };

    const group = data.find(g => g.id === componentId);

    /* ------------------ 렌더 ------------------ */
    if (loading) {
        return (
            <Box p={2} textAlign="center">
            <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2} textAlign="center">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!targetLocation || visibleParts.length === 0) {
        return (
            <Box p={2} textAlign="center">
                <Typography color="text.secondary">{t('message.common.error.text.requested.specification.information.not.exist')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
          {/* 상단 제목 + 버튼 */}
          <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {group?.name} &gt; {targetLocation.name}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleToggleAll(true)}
              >
                {t('message.common.btn.expand.all')}
              </Button>

              <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleToggleAll(false)}
              >
                {t('message.common.btn.close.all')}
              </Button>
            </Stack>
          </Stack>

          {/* Part */}
          {visibleParts.map(part => {
            const partKey = `part-${part.code}`;

            return (
            <Accordion
                key={part.code}
                expanded={expandedMap[partKey] || false}
                onChange={() =>
                setExpandedMap(prev => ({
                    ...prev,
                    [partKey]: !prev[partKey]
                }))
                }
                elevation={0}
                disableGutters
                sx={{
                mb: 2,
                border: "1px solid #ddd",
                borderRadius: 1,
                "&:before": { display: "none" }
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                    {part.name} ({part.code})
                </Typography>
                </AccordionSummary>

                <AccordionDetails>
                {part.sensorPositions.map(sensor => {
                    const sensorKey = `part-${part.code}-sensor-${sensor.sensorPosition}`;

                    return (
                    <Accordion
                        key={sensor.sensorPosition}
                        expanded={expandedMap[sensorKey] || false}
                        onChange={() =>
                            setExpandedMap(prev => ({
                                ...prev,
                                [sensorKey]: !prev[sensorKey]
                            }))
                        }
                        elevation={0}
                        disableGutters
                        sx={{
                            mt: 1,
                            ml: 2,
                            borderLeft: "3px solid #ccc",
                            backgroundColor: "rgba(0,0,0,0.02)",
                            "&:before": { display: "none" }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                fontWeight: 600,
                                color: "text.primary"
                                }}
                            >
                                {t('message.common.text.sensor.position')} : {sensor.sensorPosition}
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails sx={{ pl: 2 }}>
                            {sensor.specs.map((spec, idx) => (
                                <SpecView key={idx} spec={spec} />
                            ))}
                        </AccordionDetails>
                    </Accordion>
                    );
                })}
                </AccordionDetails>
            </Accordion>
            );
          })}
        </Box>
    );
}
